import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  RichTextContent,
  useSlotGroup,
  useCollectionBinding,
  useEditMode,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ─── Types ──────────────────────────────────────────────────────────────────

interface Review {
  name?: string | null;
  title?: string | null;
  rating?: string | number | null;
  text?: string | null;
  date?: string | null;
}

function hasValue(val: unknown): val is string {
  if (val == null) return false;
  const s = String(val).trim().toLowerCase();
  return s !== '' && s !== 'false' && s !== 'null' && s !== 'undefined';
}

// Strip any HTML tags from richtext review bodies for plain-quote rendering.
function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

const AUTO_ADVANCE_MS = 7000;
// Lines a review is clamped to before "Read more" appears — keeps slide height
// stable regardless of review length.
const CLAMP_LINES = 6;

// ─── Background slot group ──────────────────────────────────────────────────
// Pinned/parallax background image. Defaults to a property gallery photo.

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community photo' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 4,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

// ─── Component ──────────────────────────────────────────────────────────────

export function ReviewsParallax() {
  const bgImage = useSlotGroup('reviewsparallax.bg_image', bgImageDef);

  const reviews = useCollectionBinding<Review>('reviewsparallax.items', {
    collection: 'reviews',
  });

  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;

  const count = reviews?.length ?? 0;
  const [index, setIndex] = useState(0);
  const safeIndex = count > 0 ? ((index % count) + count) % count : 0;

  // Which review (if any) the visitor has expanded past the clamp.
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  // Indices whose text overflows the clamp and therefore need a "Read more".
  const [overflowing, setOverflowing] = useState<Set<number>>(new Set());
  const quoteRefs = useRef<Array<HTMLQuoteElement | null>>([]);

  // Parallax refs
  const sectionRef = useRef<HTMLElement>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);

  const goTo = useCallback(
    (next: number) => {
      if (count === 0) return;
      setExpandedIdx(null);
      setIndex(((next % count) + count) % count);
    },
    [count],
  );

  // Auto-cycle through reviews. Re-runs (resetting the timer) whenever the
  // active index changes — including after manual arrow navigation. Paused
  // while a review is expanded so the visitor can finish reading.
  useEffect(() => {
    if (count <= 1 || expandedIdx !== null) return;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % count);
    }, AUTO_ADVANCE_MS);
    return () => window.clearInterval(id);
  }, [count, index, expandedIdx]);

  // Measure which clamped reviews overflow so "Read more" only shows when
  // there's actually hidden text. Re-measures on resize (wrapping changes).
  useEffect(() => {
    const measure = () => {
      const next = new Set<number>();
      quoteRefs.current.forEach((el, i) => {
        if (el && el.scrollHeight - el.clientHeight > 1) next.add(i);
      });
      setOverflowing(next);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [count, safeIndex]);

  // Parallax: shift the oversized background image at a fraction of scroll
  // speed, creating the "pinned" depth effect as the section passes through.
  useEffect(() => {
    const section = sectionRef.current;
    const imgWrap = imgWrapRef.current;
    if (!section || !imgWrap) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      const scrolled = -rect.top;
      if (scrolled < -window.innerHeight || scrolled > section.offsetHeight) return;
      imgWrap.style.transform = `translateY(${scrolled * 0.3}px)`;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // seed position on mount (handles pre-scrolled pages)
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-hide when there are no reviews and we're not editing.
  if (count === 0 && !isEditMode) return null;

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden py-24 md:py-36 text-white"
    >
      {/* Parallax pinned background image */}
      <EditableSlotGroup groupPath="reviewsparallax.bg_image" definition={bgImageDef}>
        {/* Oversized wrapper (140% tall, starting 20% above the section top)
            gives the parallax translate room to move without exposing a gap. */}
        <div
          ref={imgWrapRef}
          className="absolute inset-x-0 h-[140%]"
          style={{ top: '-20%', willChange: 'transform' }}
        >
          {bgImage.image ? (
            <VibesitesImage
              url={bgImage.image}
              alt={bgImage.alt || ''}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
        </div>
      </EditableSlotGroup>

      {/* Dark overlay for text contrast — decorative, never intercepts clicks. */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Eyebrow / pre-title — defaults to the property name */}
        <RichTextContent
          path="reviewsparallax.subtitle"
          label="Eyebrow"
          defaultValue='<p><span data-type="field-token" data-field="property_name"></span></p>'
          as="div"
          className="font-body text-sm tracking-[0.25em] uppercase text-white/80"
        />

        {/* Divider line */}
        <div className="w-16 h-px bg-white/50 mx-auto my-6" aria-hidden="true" />

        {/* Title */}
        <RichTextContent
          path="reviewsparallax.title"
          label="Title"
          defaultValue="<p>Take Our Residents Word For It</p>"
          as="h2"
          className="font-heading text-3xl md:text-4xl leading-tight uppercase drop-shadow-lg"
        />

        {count > 0 ? (
          <div className="relative mt-12 md:mt-16">
            {count > 1 && (
              <button
                type="button"
                onClick={() => goTo(safeIndex - 1)}
                aria-label="Previous review"
                className="absolute left-0 top-1/2 -translate-y-1/2 p-2 text-white/60 hover:text-white transition-colors"
              >
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}

            <div className="overflow-hidden mx-8 md:mx-16">
              <div
                className="flex items-stretch transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${safeIndex * 100}%)` }}
              >
                {reviews?.map((review, i) => (
                  <div
                    key={i}
                    className="w-full flex-shrink-0 min-h-[14rem] flex flex-col items-center justify-center px-2"
                    aria-hidden={i !== safeIndex}
                  >
                    {hasValue(review.text) && (
                      <blockquote
                        ref={(el) => {
                          quoteRefs.current[i] = el;
                        }}
                        className="font-body text-base md:text-lg italic leading-relaxed max-w-xl mx-auto drop-shadow"
                        style={
                          expandedIdx === i
                            ? undefined
                            : {
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: CLAMP_LINES,
                                overflow: 'hidden',
                              }
                        }
                      >
                        &ldquo;{stripHtml(review.text)}&rdquo;
                      </blockquote>
                    )}
                    {overflowing.has(i) && (
                      <button
                        type="button"
                        onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}
                        // Off-screen slides are aria-hidden; keep their controls
                        // out of the tab order so the hidden subtree contains no
                        // focusable elements.
                        tabIndex={i === safeIndex ? undefined : -1}
                        className="font-body text-xs uppercase tracking-wider underline underline-offset-4 mt-4 text-white/70 hover:text-white transition-colors"
                      >
                        {expandedIdx === i ? 'Read less' : 'Read more'}
                      </button>
                    )}
                    {hasValue(review.name) && (
                      <p className="font-body text-sm mt-6 tracking-wide">— {review.name}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {count > 1 && (
              <button
                type="button"
                onClick={() => goTo(safeIndex + 1)}
                aria-label="Next review"
                className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-white/60 hover:text-white transition-colors"
              >
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}

            {/* Slide indicator dots */}
            {count > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                {reviews?.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => goTo(i)}
                    aria-label={`Go to review ${i + 1}`}
                    className={`h-2 rounded-full transition-all ${
                      i === safeIndex ? 'w-6 bg-white' : 'w-2 bg-white/40 hover:bg-white/70'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="mt-12 py-12 text-white/70">
            <p className="font-body text-lg">
              No reviews yet. Add reviews to this property to display them in this slider.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Content Schema ─────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'ReviewsParallax',
  displayName: 'Reviews Parallax',
  tokens: {
    'reviewsparallax.subtitle': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p><span data-type="field-token" data-field="property_name"></span></p>',
    },
    'reviewsparallax.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Take Our Residents Word For It</p>',
    },
  },
  slotGroups: {
    'reviewsparallax.bg_image': bgImageDef,
  },
  collectionBindings: {
    'reviewsparallax.items': {
      label: 'Reviews',
      collection: 'reviews',
    },
  },
};

(ReviewsParallax as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ReviewsParallax;
